import React, { useCallback, useEffect, useMemo } from 'react';

// Globals
import {
  // useSelector,
  useDispatch,
  useSelector,
} from 'react-redux';
import { useHistory, useParams } from 'react-router';

// UI
import {
  LoadingOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Flex, Modal, Spin, Tooltip } from 'antd';

// Locals
import useGiide from '../../hooks/useQuery/useGiide';
import useRecording from '../../hooks/useQuery/useRecording';
import styles from '../Components.module.css';
import Player from '../Player';
// import PopupModal from './PopupModal';
import SelectableArea from '../SelectableArea';

// Types
import type { GiideRouteParams } from '../../types';
import type {
  // IRecordingProcessingState,
  // IState,
  Recording,
  Step,
} from '../../graphql/types';
import { highlightCard, setGiideTabState } from '../../actions';
import useGiideContentModal from '../../hooks/useGiideContentModal';
import { previewClicked, publishClicked, StepState } from '../utils';
import usePreview from '../../hooks/usePreview';
import useUserPreference from '../../hooks/useUserPreference';
import { IState } from '../../graphql/types';
import { GetStartedModal } from './GetStartedModal';
import { colors } from '../../theme';

const antIcon = <LoadingOutlined style={{ fontSize: 64 }} spin />;

const StepTranscript: React.FC = () => {
  const dispatch = useDispatch();
  const { push } = useHistory();

  const { id } = useParams<GiideRouteParams>();

  if (!id) {
    push('/');
  }
  const { openedStep, data: giide } = useGiide(id);

  const { recording } = useRecording(openedStep?.id);

  const {
    GiideContentModal,
    setModalContent,
    ModalContent,
    tooltipMessagePreviewAndPublish,
  } = useGiideContentModal();

  const { loadPreview, previewModal, previewLoading } = usePreview();

  const { preference, updatePreference } = useUserPreference(
    'dontShowPublishInfoTooltip'
  );

  const [infoTooltipHided, setInfoTooltipHided] = React.useState<boolean>(
    !!preference
  );

  const [editingTranscript, setEditingTranscript] =
    React.useState<boolean>(false);

  const newFeedcardSaving = useSelector<IState, boolean>(
    state => !!state.newFeedcardSaving
  );
  const recordingProcessingModalVisible = useSelector<IState, boolean>(
    state => !!state.recordingProcessingModalVisible
  );

  const steps = useSelector<IState, Step[]>(state => state.steps);

  const haveAnyCardsAttached = React.useMemo(() => {
    return steps
      .map((step: Step) => ({
        cards: step?.recording?.feedCards?.items || [],
        name: step.title,
        order: step.order,
        state: step?.recording?.transcriptResults
          ? StepState.done
          : StepState.new,
        allowRelinking:
          !step?.recording?.deleted && step?.recording?.transcriptResults,
      }))
      .map(o => o.cards.length > 0)
      .filter(o => o)[0];
  }, [steps]);

  useEffect(() => {
    dispatch(highlightCard(null));
  }, [dispatch]);

  const handleDontShowAgain = useCallback(() => {
    updatePreference(true);
    setInfoTooltipHided(true);
  }, [updatePreference]);

  const handlePreview = useCallback(async () => {
    await previewClicked(loadPreview);
  }, [loadPreview]);

  const publishTooltip = useMemo(() => {
    if (!!tooltipMessagePreviewAndPublish) {
      return tooltipMessagePreviewAndPublish;
    }

    if (!infoTooltipHided) {
      return (
        <div>
          This will generate a link you can share with your audience. Your giide
          will not be posted anywhere else.
          <div className="dontShowAgainCheckboxTooltip">
            <Checkbox
              onChange={handleDontShowAgain}
              className="hoveringInfoTooltipCheckbox"
            />
            Don’t show again
          </div>
        </div>
      );
    }
  }, [tooltipMessagePreviewAndPublish, infoTooltipHided, handleDontShowAgain]);

  const [confirmModal, contextHolder] = Modal.useModal();

  return (
    <>
      {contextHolder}
      {previewModal()}
      <GetStartedModal />
      <div className={styles.giideContent}>
        <>
          <div className={styles.infoText}>
            <p style={{ color: colors.black3 }}>
              <InfoCircleOutlined />{' '}
              {editingTranscript
                ? 'See some typos in your transcript? Click on a word to fix the spelling.'
                : 'Click on a word to attach a feedcard. Fix typos by clicking the edit button to the right.'}
            </p>
            <Tooltip
              title={
                editingTranscript
                  ? 'Done editing transcript'
                  : 'Edit transcript'
              }
            >
              <Button
                type={'text'}
                onClick={() => setEditingTranscript(!editingTranscript)}
                icon={editingTranscript ? <CheckOutlined /> : <EditOutlined />}
              ></Button>
            </Tooltip>
          </div>
        </>

        {openedStep &&
        recording &&
        !recording.deleted &&
        recording.transcriptResults ? (
          <>
            <SelectableArea
              recordId={recording.id}
              isInLibrary={openedStep.isInLibrary}
              editingTranscript={editingTranscript}
            />
            {!editingTranscript && (
              <Player
                giideId={id}
                recording={recording as Recording}
                disabledDelete={openedStep.isInLibrary}
              />
            )}
            {newFeedcardSaving && (
              <div className={styles.newFeedcardSaving}>Saving...</div>
            )}
            <div
              className={styles.publishBtnWrapper}
              style={editingTranscript ? { justifyContent: 'flex-end' } : {}}
            >
              {!editingTranscript && (
                <div>
                  <Button
                    onClick={() => {
                      dispatch(setGiideTabState('audio'));
                    }}
                  >
                    Back
                  </Button>
                </div>
              )}

              {editingTranscript ? (
                <div>
                  <Button onClick={() => setEditingTranscript(false)}>
                    Back to feedcards
                  </Button>
                </div>
              ) : (
                <Flex gap={'middle'}>
                  <Tooltip
                    placement={'bottom'}
                    title={tooltipMessagePreviewAndPublish || ''}
                  >
                    <Button
                      onClick={handlePreview}
                      loading={previewLoading}
                      disabled={
                        !!tooltipMessagePreviewAndPublish || previewLoading
                      }
                    >
                      Preview
                    </Button>
                  </Tooltip>

                  <Tooltip title={tooltipMessagePreviewAndPublish || ''}>
                    <Tooltip placement="bottom" title={publishTooltip}>
                      <Button
                        type={'primary'}
                        onClick={() =>
                          publishClicked(
                            setModalContent,
                            ModalContent,
                            dispatch,
                            giide,
                            haveAnyCardsAttached,
                            confirmModal
                          )
                        }
                        disabled={
                          !!tooltipMessagePreviewAndPublish || previewLoading
                        }
                      >
                        Next
                      </Button>
                    </Tooltip>
                  </Tooltip>
                </Flex>
              )}
            </div>
          </>
        ) : !recording && !recordingProcessingModalVisible ? (
          <Spin
            indicator={antIcon}
            style={{ position: 'absolute', left: '50%', marginLeft: -32 }}
          />
        ) : null}
      </div>

      <GiideContentModal />
    </>
  );
};

export default StepTranscript;
